import axios from 'axios';
import { API_BASE_URL } from '../../config/env';
import { clearCustomerAuthFlow } from '../storage/customerFlow';
import { attachRoleAuthInterceptors } from './authInterceptors';
import { FINISHED_STATUSES } from '../../types/booking';
import type {
  ActiveInstantTripSummary,
  ActiveTripSummary,
  BookingHistoryRequest,
  BookingSummary,
  RouteSummaryRequest,
  RouteSummaryResponse,
  TripDetail,
  TripTrackingLatestLocation,
  TripTrackingSnapshot,
  TripRequestPayload,
  TripSearchResponse,
  UpcomingAssignmentTripSummary,
} from '../../types/booking';

/**
 * Customer booking and trip endpoints.
 *
 * Paths match the user app's `deliveryService` exactly — same backend, same
 * routes. Verified: the API allows browser origins on these services, including
 * the `authorization` header and credentials, so no proxy is needed.
 *
 * Auth is the existing customer-role interceptor pair, not a new mechanism:
 * bearer token, silent refresh, and one retry on 401 all come for free, and a
 * second auth path would be a second thing to get wrong.
 */

const api = axios.create({ baseURL: API_BASE_URL });

attachRoleAuthInterceptors(api, {
  role: 'customer',
  clearSession: clearCustomerAuthFlow,
});

/**
 * Bridges a standard `AbortSignal` onto axios 0.21's `CancelToken`.
 *
 * This axios predates `signal` support (added in 0.22), but callers should not
 * inherit that: the app aborts a superseded route request so a slow earlier
 * reply cannot overwrite a newer one, and the web needs the same guarantee.
 * Keeping `AbortSignal` at the boundary means upgrading axios later deletes
 * this function rather than changing every call site.
 */
function cancelTokenFor(signal?: AbortSignal) {
  if (!signal) return undefined;

  const source = axios.CancelToken.source();

  if (signal.aborted) {
    source.cancel('aborted');
  } else {
    signal.addEventListener('abort', () => source.cancel('aborted'), { once: true });
  }

  return source.token;
}

/** The backend returns lists; the app's older type says single. Accept both. */
function asArray<T>(value: unknown): T[] {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? (value as T[]) : [value as T];
}

/**
 * Active and upcoming trips for the signed-in customer.
 *
 * 204 means "no trips" rather than an error, which is why the status check is
 * widened — the app does the same. Returning an empty summary rather than null
 * keeps callers free of a null branch for what is a normal, common state.
 */
export async function getActiveTripSummary(): Promise<ActiveTripSummary> {
  const response = await api.get('/booking-management/api/active', {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 204,
  });

  if (response.status === 204 || !response.data) {
    return { activeTrips: [], upcomingTrips: [] };
  }

  const data = response.data as Record<string, unknown>;

  return {
    activeTrips: asArray<ActiveInstantTripSummary>(data.activeInstantTrip),
    /* Both spellings are accepted during the backend's rollout, exactly as the
       app does — dropping either silently empties the list. */
    upcomingTrips: asArray<UpcomingAssignmentTripSummary>(
      data.upcomingAssignmentTrips ?? data.upcomingAssignmentTrip,
    ),
  };
}

/**
 * Finished bookings for a customer.
 *
 * A 404 means "none", not a failure — the app treats it that way and a history
 * page that showed an error for a new customer would be simply wrong.
 */
export async function listFinishedBookings(userId: string): Promise<BookingSummary[]> {
  /* `userid` is the backend's spelling. Sending `userId` returns nothing.
     Typed as a const rather than with `satisfies`: this project builds through
     Vite 2's esbuild, which cannot parse TS 4.9 syntax even though the
     installed `tsc` accepts it. */
  const payload: BookingHistoryRequest = { userid: userId, statuses: FINISHED_STATUSES };

  const response = await api.post('booking-management/api/book/bookings/my', payload, {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
  });

  if (response.status === 404 || !response.data) return [];

  return (response.data as BookingSummary[]) ?? [];
}

/**
 * The full record of one trip — route geometry, fare, load notes, attachments
 * and the drivers who were on it.
 *
 * Same endpoint the app's "Offer Status" screen calls, `code` included: the
 * backend resolves a trip by either key and matching the app's call exactly
 * keeps the two screens showing the same trip. Missing is `null`, not a throw —
 * a customer following an old link should be told the trip is gone rather than
 * shown a failure they cannot act on.
 */
export async function getTripDetail(tripId: string, code?: string | null): Promise<TripDetail | null> {
  const response = await api.get('booking-management/api/book/bookings/offer-status', {
    params: code ? { tripId, code } : { tripId },
    validateStatus: (status) => (status >= 200 && status < 300) || status === 204 || status === 404,
  });

  if (response.status === 204 || response.status === 404 || !response.data) return null;

  return response.data as TripDetail;
}

/**
 * Requests an instant trip and starts the driver search.
 *
 * THE IDEMPOTENCY KEY IS LOAD-BEARING. It is part of the URL, and the backend
 * uses it to collapse retries onto one trip. Generate it once per booking
 * attempt and reuse it for every retry of that attempt — a fresh key on retry
 * is a second trip and a second card authorisation. Never derive it from
 * something that changes between attempts.
 *
 * The payload is snake_case: that is the wire contract, not a style choice.
 */
export async function startTripSearch(
  userId: string,
  idempotencyKey: string,
  payload: TripRequestPayload,
): Promise<TripSearchResponse> {
  const response = await api.post(
    `booking-management/api/book/request/${encodeURIComponent(userId)}/${encodeURIComponent(
      idempotencyKey,
    )}`,
    payload,
  );

  return response.data as TripSearchResponse;
}

/** Abandons a search in progress. Safe to call when it has already ended. */
export async function cancelTripSearch(tripId: string): Promise<void> {
  await api.delete(`booking-management/api/book/request/${encodeURIComponent(tripId)}`, {
    validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
  });
}

export async function getTripTrackingState(tripId: string): Promise<TripTrackingSnapshot | null> {
  const response = await api.get(
    `booking-management/api/v1/trip-tracking/${encodeURIComponent(tripId)}`,
    {
      validateStatus: (status) => (status >= 200 && status < 300) || status === 204 || status === 404,
    },
  );

  if (response.status === 204 || response.status === 404 || !response.data) return null;

  return response.data as TripTrackingSnapshot;
}

export async function getTripTrackingLatestLocation(
  tripId: string,
): Promise<TripTrackingLatestLocation | null> {
  const response = await api.get(
    `booking-management/api/v1/trip-tracking/${encodeURIComponent(tripId)}/latest-location`,
    {
      validateStatus: (status) => (status >= 200 && status < 300) || status === 204 || status === 404,
    },
  );

  if (response.status === 204 || response.status === 404 || !response.data) return null;

  return response.data as TripTrackingLatestLocation;
}

export async function getTripLastKnownDriverLocation(
  tripId: string,
): Promise<TripTrackingLatestLocation | null> {
  const response = await api.get(
    `booking-management/api/trips/${encodeURIComponent(tripId)}/last-location`,
    {
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    },
  );

  if (response.status === 404 || !response.data) return null;

  return response.data as TripTrackingLatestLocation;
}

/** Distance, duration and encoded geometry for a route. */
export async function getRouteSummary(
  payload: RouteSummaryRequest,
  options?: { signal?: AbortSignal },
): Promise<RouteSummaryResponse> {
  const response = await api.post('driver-management/route/summary', payload, {
    cancelToken: cancelTokenFor(options?.signal),
  });

  return response.data as RouteSummaryResponse;
}

export default api;
